import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PaziaButton } from "components/atoms";
import { UserOption, useUsersOptions } from "api/hooks/useUsers";
import { EmailRecipientApi } from "api/types/EmailRecipient";
import { MessageRecipientRequest } from "api/types/ScheduledMessage";
import { useRecipientSelection, RecipientSelectionControls, RecipientListItem } from "components/RecipientSelector";

interface MessageRecipientSelectorProps {
  name?: string;
  error?: string;
  /**
   * Optional array of user IDs to prepopulate the recipient list.
   * If not provided, will check for 'recipients' query parameter (comma-separated IDs).
   * Example URL: /scheduled-messages/new?recipients=123,456,789
   * Note: Only users with phone numbers will be included.
   */
  initialRecipientIds?: number[];
}

export const MessageRecipientSelector = ({
  name = "recipients",
  error,
  initialRecipientIds,
}: MessageRecipientSelectorProps) => {
  const { options: userOptions } = useUsersOptions();
  const [searchParams] = useSearchParams();

  // Get recipient IDs from query params (comma-separated) or from props
  const recipientIdsFromQuery = useMemo(() => {
    const recipientsParam = searchParams.get("recipients");
    if (recipientsParam) {
      return recipientsParam
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));
    }
    return undefined;
  }, [searchParams]);

  const finalInitialRecipientIds = initialRecipientIds || recipientIdsFromQuery;

  // Filter function to only show users with phone numbers
  const hasRequiredContactMethod = (option: UserOption): boolean => !!option.user?.phone;

  const {
    selectionMode,
    pendingRecipients,
    currentRecipients,
    isLoading,
    handleModeChange,
    handleManualRecipientsChange,
    handleConfirmSelection,
    handleRemoveRecipient,
    handleAddToManual,
    handleClearRecipients,
    courseOptions,
    courseGroupOptions,
    availableUserOptions,
    coursesLoading,
    courseGroupsLoading,
  } = useRecipientSelection<MessageRecipientRequest>({
    name,
    convertFromEmailRecipient: (recipient: EmailRecipientApi): MessageRecipientRequest => {
      const userOption = userOptions.find((opt) => parseInt(String(opt.value)) === recipient.id);
      return {
        userId: recipient.id,
        contactValue: userOption?.user?.phone || "",
      };
    },
    getUserId: (recipient: MessageRecipientRequest) => recipient.userId,
    filterUserOption: hasRequiredContactMethod,
    transformManualSelection: (userId: number, option: UserOption): MessageRecipientRequest => {
      return {
        userId,
        contactValue: option.user?.phone || "",
      };
    },
    initialRecipientIds: finalInitialRecipientIds,
  });

  // Get user display name for recipient
  const getUserDisplayName = (userId: number): string => {
    const user = userOptions.find((option) => parseInt(String(option.value)) === userId);
    return user ? user.label : `User ${userId}`;
  };

  // Convert to display format
  const displayRecipients: RecipientListItem[] = useMemo(() => {
    return currentRecipients
      .filter((r) => r.contactValue) // Only show recipients with valid contact info
      .map((recipient) => ({
        id: recipient.userId,
        fullName: getUserDisplayName(recipient.userId),
        contactValue: recipient.contactValue,
      }));
  }, [currentRecipients, userOptions]);

  const totalAvailableUsers = userOptions.length;
  const missingContactCount = totalAvailableUsers - userOptions.filter(hasRequiredContactMethod).length;

  return (
    <div className="space-y-4">
      <div>
        {/* Main content in a single row */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Left side - Selection controls */}
          <div className="space-y-4">
            {/* Contact method info */}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-700 dark:bg-blue-900/20">
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <div className="font-medium">Wyślij SMS do kursantów</div>
                {missingContactCount > 0 && (
                  <div className="mt-1 text-xs text-orange-700 dark:text-orange-400">
                    Uwaga: {missingContactCount} użytkowników nie ma wymaganego kontaktu i zostanie pominiętych
                  </div>
                )}
              </div>
            </div>

            {/* Selection Controls */}
            <RecipientSelectionControls
              selectionMode={selectionMode}
              onModeChange={handleModeChange}
              courseOptions={courseOptions}
              coursesLoading={coursesLoading}
              courseGroupOptions={courseGroupOptions}
              courseGroupsLoading={courseGroupsLoading}
              availableUserOptions={availableUserOptions}
              pendingRecipients={pendingRecipients}
              onManualRecipientsChange={handleManualRecipientsChange}
              onConfirmSelection={handleConfirmSelection}
              showModeDescriptions={false}
              customLabels={{
                manual: "Wybierz ręcznie",
                course: "Z kursu",
                coursegroup: "Z grupy kursów",
              }}
            />

            {/* Validation Message */}
            <div className="rounded-md bg-gray-50 p-3 dark:bg-gray-800">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Limit odbiorców:</strong> Możesz wybrać maksymalnie 1000 odbiorców na jedną wiadomość.
              </p>
              <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                Aktualnie wybrano: {displayRecipients.length} / 1000 odbiorców
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
          </div>

          {/* Right side - Recipients Preview */}
          {displayRecipients.length > 0 && (
            <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 dark:bg-green-500">
                    <span className="text-xs font-bold text-white">✓</span>
                  </div>
                  <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">
                    Finalna lista odbiorców ({displayRecipients.length}){isLoading && " - ładowanie..."}
                  </h4>
                </div>
                {(selectionMode === "course" || selectionMode === "coursegroup") && (
                  <PaziaButton type="tertiary" onClick={handleAddToManual} disabled={isLoading}>
                    Dodaj do wyboru ręcznego
                  </PaziaButton>
                )}
              </div>

              <p className="mb-3 text-xs text-green-700 dark:text-green-300">
                <strong>Te osoby otrzymają SMS:</strong> Lista poniżej zawiera wszystkich odbiorców, którzy dostaną
                zaplanowaną wiadomość.
              </p>

              <div className="max-h-96 overflow-y-auto rounded border border-green-300 bg-white dark:border-green-700 dark:bg-gray-900">
                {displayRecipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center justify-between border-b border-green-100 p-2 last:border-b-0 dark:border-green-800"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{recipient.fullName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Telefon: {recipient.contactValue}</div>
                    </div>
                    {selectionMode === "manual" && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(recipient.id)}
                        className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        title="Usuń odbiorcę"
                      >
                        <span className="text-xs">✕</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {selectionMode === "manual" && displayRecipients.length > 0 && (
                <div className="mt-2">
                  <PaziaButton type="tertiary" onClick={handleClearRecipients} className="w-full">
                    Wyczyść wszystkich
                  </PaziaButton>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
